import { supabase } from '../supabase';
import type { User, Session } from '@supabase/supabase-js';

export interface AuthResponse {
  user: User | null;
  session: Session | null;
  error: any;
}

export const authApi = {
  // Sign in with email and password
  async signIn(email: string, password: string): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
      }

      return {
        user: data.user,
        session: data.session,
        error,
      };
    } catch (err) {
      console.error('Sign in exception:', err);
      return {
        user: null,
        session: null,
        error: err,
      };
    }
  },

  // Sign out with improved error handling
  async signOut(): Promise<{ error: any }> {
    try {
      console.log('Attempting to sign out...');
      
      // Clear local storage first to prevent stale state
      localStorage.removeItem('sb-auth-token');
      sessionStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        // Even if there's an error, we've cleared local storage
        // so the user will appear logged out on the client side
      } else {
        console.log('Sign out successful');
      }

      return { error };
    } catch (err) {
      console.error('Sign out exception:', err);
      // Clear local storage even on exception
      localStorage.removeItem('sb-auth-token');
      sessionStorage.clear();
      return { error: err };
    }
  },

  // Get current session
  async getSession(): Promise<{ session: Session | null; error: any }> {
    try {
      const { data, error } = await supabase.auth.getSession();
      return {
        session: data.session,
        error,
      };
    } catch (err) {
      console.error('Get session error:', err);
      return {
        session: null,
        error: err,
      };
    }
  },

  // Get current user
  async getUser(): Promise<{ user: User | null; error: any }> {
    try {
      const { data, error } = await supabase.auth.getUser();
      return {
        user: data.user,
        error,
      };
    } catch (err) {
      console.error('Get user error:', err);
      return {
        user: null,
        error: err,
      };
    }
  },

  // Listen to auth state changes
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change:', event, session?.user?.id);
      callback(event, session);
    });
  },

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session?.user;
    } catch (err) {
      console.error('Authentication check error:', err);
      return false;
    }
  },

  // Refresh session
  async refreshSession(): Promise<{ session: Session | null; error: any }> {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      return {
        session: data.session,
        error,
      };
    } catch (err) {
      console.error('Refresh session error:', err);
      return {
        session: null,
        error: err,
      };
    }
  },
};